"use client";

import Link from "next/link";
import React, { ReactNode } from "react";
import ErrorBoundary from "./ErrorBoundary";

interface RouteErrorBoundaryProps {
  children: ReactNode;
  routeName?: string;
}

/**
 * RouteErrorBoundary handles errors that occur during route changes.
 * It ensures navigation errors are gracefully managed and displayed.
 */
const RouteErrorBoundary: React.FC<RouteErrorBoundaryProps> = ({
  children,
  routeName = "this page",
}) => {
  const handleError = (error: Error) => {
    // Log route-specific errors
    console.error(`Error in route "${routeName}":`, error);

    // You can send route-specific error analytics here
    // Example: analytics.track('Route Error', { route: routeName, error: error.message });
  };

  const fallbackUI = (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="max-w-lg w-full text-center">
        <div className="mb-6">
          <svg
            className="mx-auto h-16 w-16 text-red-500"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
            />
          </svg>
        </div>
        <h1 className="text-3xl font-bold text-foreground mb-4">
          Page Unavailable
        </h1>
        <p className="text-lg text-muted-foreground mb-8">
          We&apos;re having trouble loading {routeName}. This might be a
          temporary issue.
        </p>
        <div className="space-y-4">
          <button
            onClick={() => window.location.reload()}
            className="w-full bg-blue-600 text-white py-3 px-6 rounded hover:bg-blue-700 transition-colors font-medium"
          >
            Reload Page
          </button>
          <button
            onClick={() => window.history.back()}
            className="w-full bg-primary text-primary-foreground py-3 px-6 rounded hover:bg-primary/90 transition-colors font-medium"
          >
            Go Back
          </button>
          <Link
            href="/"
            className="inline-block w-full bg-secondary text-secondary-foreground py-3 px-6 rounded hover:bg-secondary/80 transition-colors font-medium"
          >
            Return Home
          </Link>
        </div>
      </div>
    </div>
  );

  return (
    <ErrorBoundary fallback={fallbackUI} onError={handleError}>
      {children}
    </ErrorBoundary>
  );
};

export default RouteErrorBoundary;
